import get from 'lodash.get'
import { FACTSHEET_VALUE_VALIDATION_ERRORS } from './constants'

class FactsheetValidator {

  static REGEX_TYPE_INTEGER = /^(0|[-+]?[1-9]\d*)$/
  static REGEX_TYPE_NUMBER = /^(\d+\.?\d*|\.\d+)$/

  // numbers can be of format .01 as well

  constructor () {
    this.regexes = {}
  }

  /**
   * Performs validation and returns an object indicating the validity along with an error message if applicable
   *
   * @param attributeValue
   * @param attributeDefinition
   * @returns {{valid: boolean, reason?: string}} The reason is an element in `FACTSHEET_VALUE_VALIDATION_ERRORS`
   */
  validateValue (attributeValue, attributeDefinition) {
    const type = get(attributeDefinition, 'value.type', null)
    switch (type) {
      case 'INTEGER':
        return { valid: this.getRegexObj(FactsheetValidator.REGEX_TYPE_INTEGER).test(attributeValue) }
      case 'NUMBER':
        return { valid: this.getRegexObj(FactsheetValidator.REGEX_TYPE_NUMBER).test(attributeValue) }
      case 'URL':
        return this.validateUrlValue(attributeValue)
      case 'PRICE':
        return { valid: this.validatePriceValue(attributeValue) !== false }
      default:
        return { valid: true }
    }
  }

  /**
   * Validates a string input based on the same rules used by the backend
   *
   * @param {string | null | undefined} inputUrl
   * @returns {{valid: boolean, reason?: string}}
   */
  validateUrlValue (inputUrl) {
    if (!inputUrl) {
      return { valid: false }
    }

    const trimmedInputUrl = inputUrl.trim()
    if (trimmedInputUrl === '') {
      return { valid: false }
    }

    // Check if the URL starts with "https://".
    if (!trimmedInputUrl.startsWith('https://')) {
      return { valid: false, reason: FACTSHEET_VALUE_VALIDATION_ERRORS.URL_NON_HTTPS }
    }

    try {
      // Encode non-ASCII characters and whitespace.
      // We find all characters that are not standard printable ASCII and encode them.
      const encodedUrl = trimmedInputUrl.replace(/[^\x21-\x7F]/g, (match) => {
        return encodeURIComponent(match)
      })

      // Validate the overall URL structure.
      new URL(encodedUrl)

      // Check if the URL's byte length is less than 256.
      const byteLength = new TextEncoder().encode(encodedUrl).length
      if (byteLength >= 256) {
        return { valid: false, reason: FACTSHEET_VALUE_VALIDATION_ERRORS.URL_TOO_LONG }
      }

    } catch (error) {
      // If the URL constructor throws an error (e.g., TypeError for invalid URL),
      // or if any other error occurs, the URL is considered invalid.
      return { valid: false }
    }

    // If all checks pass, the URL is valid.
    return { valid: true }
  }

  /**
   * @param regex
   * @returns RegExp
   */
  getRegexObj (regex) {
    if (!this.regexes.hasOwnProperty(regex)) {
      this.regexes[regex] = new RegExp(regex)
    }
    return this.regexes[regex]
  }

  validatePriceValue (attributeValue) {
    return this.getRegexObj(FactsheetValidator.REGEX_TYPE_NUMBER).test(attributeValue)
      && parseFloat(attributeValue) >= 0.1
  }
}

export const factsheetValidator = new FactsheetValidator()
